/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import { Modal, Grid, Autocomplete, TextField, Chip } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import Dropdown from "components/CustomDropdown/Dropdown";
import { CloudUpload, InfoOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "utils/api";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Configure dayjs for IST timezone
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Kolkata");

function EditAttendanceModal({ open, onClose, attendance, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    topicCovered: "",
    type: [],
    homeWorkStatus: "",
    attendanceTime: "",
    startTime: "",
    endTime: "",
    homeWork: null,
    voiceNote: null,
  });
  const [duration, setDuration] = useState(0);

  // Utility function to format time to 12-hour AM/PM format
  const formatTimeTo12Hour = (timeString) => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Utility function to format date to DD/MM/YYYY
  const formatDateToIndian = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    if (attendance && open) {
      console.log("Attendance data:", attendance);
      console.log("Start Time:", attendance.startTime);
      console.log("End Time:", attendance.endTime);

      // Format attendance date (date only)
      const attendanceDate = attendance.attendanceTime
        ? (() => {
            const date = new Date(attendance.attendanceTime);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
          })()
        : "";

      // Format start time (time only)
      const startTime = attendance.startTime
        ? (() => {
            const date = new Date(attendance.startTime);
            const hours = String(date.getHours()).padStart(2, "0");
            const minutes = String(date.getMinutes()).padStart(2, "0");
            return `${hours}:${minutes}`;
          })()
        : "";

      // Format end time (time only)
      const endTime = attendance.endTime
        ? (() => {
            const date = new Date(attendance.endTime);
            const hours = String(date.getHours()).padStart(2, "0");
            const minutes = String(date.getMinutes()).padStart(2, "0");
            console.log("Formatted endTime:", `${hours}:${minutes}`);
            return `${hours}:${minutes}`;
          })()
        : "";

      // Handle type - ensure it's an array
      let typeValue = [];
      if (Array.isArray(attendance.type)) {
        typeValue = attendance.type;
      } else if (attendance.type) {
        typeValue = [attendance.type];
      }

      setFormData({
        topicCovered: attendance.topicCovered || "",
        type: typeValue,
        homeWorkStatus: attendance.homeWorkStatus || "",
        attendanceTime: attendanceDate,
        startTime: startTime,
        endTime: endTime,
        homeWork: null,
        voiceNote: null,
      });

      // Calculate initial duration
      if (startTime && endTime) {
        calculateDuration(startTime, endTime);
      }
    }
  }, [attendance, open]);

  // Calculate duration between start and end time
  const calculateDuration = (start, end) => {
    if (!start || !end) {
      setDuration(0);
      return;
    }

    const [startHours, startMinutes] = start.split(":").map(Number);
    const [endHours, endMinutes] = end.split(":").map(Number);

    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;

    const durationMinutes = endTotalMinutes - startTotalMinutes;
    setDuration(durationMinutes > 0 ? durationMinutes : 0);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Validate end time if it's being changed
    if (name === "endTime" && formData.startTime && value) {
      if (value < formData.startTime) {
        toast.error("End time cannot be less than start time");
        return;
      }
      calculateDuration(formData.startTime, value);
    }

    // Calculate duration when start time changes
    if (name === "startTime" && formData.endTime && value) {
      if (formData.endTime < value) {
        toast.error("Start time cannot be greater than end time");
        return;
      }
      calculateDuration(value, formData.endTime);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (field, file) => {
    if (file) {
      setFormData((prev) => ({
        ...prev,
        [field]: file,
      }));
    }
  };

  const handleDropdownChange = (name, value) => {
    // Extract the actual value if it's an event object
    const actualValue = value?.target ? value.target.value : value;

    setFormData((prev) => ({
      ...prev,
      [name]: actualValue,
    }));
  };

  const handleTypeChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      type: newValue.map((option) => option.value),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.topicCovered?.trim()) {
      toast.error("Home Note is required");
      return;
    }

    setLoading(true);

    try {
      const payload = new FormData();
      payload.append("attendanceId", attendance.id || attendance._id);
      payload.append("topicCovered", formData.topicCovered.trim());

      // Add type array if valid
      if (formData.type && Array.isArray(formData.type) && formData.type.length > 0) {
        formData.type.forEach((type) => {
          payload.append("type", type);
        });
      }

      // Only add homeWorkStatus if it's a valid string value
      if (formData.homeWorkStatus && typeof formData.homeWorkStatus === "string") {
        payload.append("homeWorkStatus", formData.homeWorkStatus);
      }

      // Handle attendance date (date only)
      if (formData.attendanceTime) {
        const dateValue = new Date(formData.attendanceTime);
        payload.append("attendanceTime", dateValue.toISOString());
      }

      // Handle start time - combine with attendance date in IST
      if (formData.startTime) {
        const dateStr = formData.attendanceTime || new Date().toISOString().split("T")[0];
        const startDateTime = dayjs.tz(`${dateStr}T${formData.startTime}:00`, "Asia/Kolkata");
        payload.append("startTime", startDateTime.toISOString());
      }

      // Handle end time - combine with attendance date in IST
      if (formData.endTime) {
        const dateStr = formData.attendanceTime || new Date().toISOString().split("T")[0];
        const endDateTime = dayjs.tz(`${dateStr}T${formData.endTime}:00`, "Asia/Kolkata");
        payload.append("endTime", endDateTime.toISOString());
      }

      // Add files if selected
      if (formData.homeWork) {
        payload.append("homeWork", formData.homeWork);
      }

      if (formData.voiceNote) {
        payload.append("voiceNote", formData.voiceNote);
      }

      const response = await api.put(`/attendance/${attendance.id || attendance._id}`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        toast.success("Attendance updated successfully");
        onUpdate();
        onClose();
      }
    } catch (error) {
      console.error("Error updating attendance:", error);
      const validationErrors = error.response?.data?.errors;
      toast.error(
        Array.isArray(validationErrors) && validationErrors.length
          ? validationErrors.join("; ")
          : error.response?.data?.message || "Failed to update attendance"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      topicCovered: "",
      type: [],
      homeWorkStatus: "",
      attendanceTime: "",
      startTime: "",
      endTime: "",
      homeWork: null,
      voiceNote: null,
    });
    setDuration(0);
    onClose();
  };

  const typeOptions = [
    { label: "Regular", value: "REGULAR" },
    { label: "Extra", value: "EXTRA" },
    { label: "Recovery", value: "RECOVERY" },
  ];

  const attendanceTypeOptions = [
    { value: "REGULAR", label: "Regular Class" },
    { value: "EXTRA", label: "Extra Class" },
    { value: "RECOVERY", label: "Recovery Class" },
  ];

  const homeWorkStatusOptions = [
    { label: "Done", value: "DONE" },
    { label: "Incomplete", value: "INCOMPLETE" },
  ];

  return (
    <Modal open={open} onClose={handleClose}>
      <ArgonBox
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: "80%", md: "600px" },
          background: "linear-gradient(195deg, #49a3f1, #1A73E8)",
          borderRadius: "20px",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
        }}
      >
        <ArgonBox
          sx={{
            background: "white",
            borderRadius: "20px",
            m: 2,
            p: 3,
          }}
        >
          <ArgonTypography variant="h5" fontWeight="bold" mb={3}>
            Edit Attendance
          </ArgonTypography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ArgonBox mb={1}>
                  <ArgonTypography variant="caption" fontWeight="bold">
                    Home Work Note *
                  </ArgonTypography>
                </ArgonBox>
                <ArgonInput
                  type="text"
                  name="topicCovered"
                  value={formData.topicCovered}
                  onChange={handleInputChange}
                  placeholder="Enter home note"
                  fullWidth
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ArgonBox mb={1}>
                  <ArgonTypography variant="caption" fontWeight="bold">
                    Attendance Type *
                  </ArgonTypography>
                </ArgonBox>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  id="attendance-type-autocomplete"
                  options={attendanceTypeOptions}
                  value={attendanceTypeOptions.filter((option) =>
                    formData.type.includes(option.value)
                  )}
                  onChange={handleTypeChange}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select attendance types"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          minHeight: "45px",
                          borderRadius: "8px",
                        },
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.value}
                        label={option.label}
                        size="small"
                        sx={{
                          backgroundColor: "#5e72e4",
                          color: "white",
                          "& .MuiChip-deleteIcon": {
                            color: "rgba(255, 255, 255, 0.7)",
                            "&:hover": {
                              color: "white",
                            },
                          },
                        }}
                      />
                    ))
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      padding: "5px",
                    },
                  }}
                />
              </Grid>

              {/* Important Note about Start and End Time */}
              <Grid item xs={12}>
                <ArgonBox
                  sx={{
                    backgroundColor: "#E3F2FD",
                    border: "1px solid #2196F3",
                    borderRadius: "8px",
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <InfoOutlined sx={{ fontSize: 20, color: "#2196F3" }} />
                  <ArgonTypography variant="caption" fontWeight="medium" color="info">
                    Important: Please provide Start Time and End Time according to each Type
                  </ArgonTypography>
                </ArgonBox>
              </Grid>

              <Grid item xs={12} sm={6}>
                <ArgonBox mb={1}>
                  <ArgonTypography variant="caption" fontWeight="bold">
                    Homework Status
                  </ArgonTypography>
                </ArgonBox>
                <Dropdown
                  options={homeWorkStatusOptions}
                  value={formData.homeWorkStatus}
                  onChange={(value) => handleDropdownChange("homeWorkStatus", value)}
                  placeholder="Select status"
                />
              </Grid>

              <Grid item xs={12}>
                <ArgonBox mb={1}>
                  <ArgonTypography variant="caption" fontWeight="bold">
                    Attendance Date
                  </ArgonTypography>
                  {formData.attendanceTime && (
                    <ArgonTypography variant="caption" color="info" ml={1}>
                      ({formatDateToIndian(formData.attendanceTime)})
                    </ArgonTypography>
                  )}
                </ArgonBox>
                <ArgonInput
                  type="date"
                  name="attendanceTime"
                  value={formData.attendanceTime}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ArgonBox mb={1}>
                  <ArgonTypography variant="caption" fontWeight="bold">
                    Start Time
                  </ArgonTypography>
                  {formData.startTime && (
                    <ArgonTypography variant="caption" color="info" ml={1}>
                      ({formatTimeTo12Hour(formData.startTime)})
                    </ArgonTypography>
                  )}
                </ArgonBox>
                <ArgonInput
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ArgonBox mb={1}>
                  <ArgonTypography variant="caption" fontWeight="bold">
                    End Time
                  </ArgonTypography>
                  {formData.endTime && (
                    <ArgonTypography variant="caption" color="info" ml={1}>
                      ({formatTimeTo12Hour(formData.endTime)})
                    </ArgonTypography>
                  )}
                </ArgonBox>
                <ArgonInput
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleInputChange}
                  inputProps={{
                    min: formData.startTime || undefined,
                  }}
                  fullWidth
                />
              </Grid>

              {/* Duration Display */}
              {duration > 0 && (
                <Grid item xs={12}>
                  <ArgonBox
                    sx={{
                      backgroundColor: "#E3F2FD",
                      border: "1px solid #2196F3",
                      borderRadius: "8px",
                      p: 1.5,
                    }}
                  >
                    <ArgonTypography variant="caption" fontWeight="bold" color="info">
                      Duration: {Math.floor(duration / 60)}h {duration % 60}m
                    </ArgonTypography>
                  </ArgonBox>
                </Grid>
              )}

              {/* File Uploads */}
              <Grid item xs={12} sm={6}>
                <ArgonBox>
                  <ArgonTypography variant="caption" fontWeight="bold" mb={1} display="block">
                    Homework Assignment
                  </ArgonTypography>

                  {/* Show existing file if available */}
                  {attendance?.homeWork && (
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="caption" color="text" mb={0.5} display="block">
                        Current File:
                      </ArgonTypography>
                      <ArgonBox
                        sx={{
                          border: "1px solid #e9ecef",
                          borderRadius: 2,
                          p: 1.5,
                          background: "#f8f9fa",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <ArgonTypography variant="caption" fontWeight="medium">
                          📄 Uploaded File
                        </ArgonTypography>
                        <ArgonButton
                          component="a"
                          href={attendance.homeWork}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="text"
                          color="info"
                          size="small"
                          sx={{ fontSize: "11px", py: 0.5, px: 1 }}
                        >
                          View
                        </ArgonButton>
                      </ArgonBox>
                    </ArgonBox>
                  )}

                  {/* Upload section */}
                  <ArgonTypography variant="caption" color="text" mb={0.5} display="block">
                    {attendance?.homeWork ? "Upload New Homework" : "Upload Homework"}
                  </ArgonTypography>
                  <ArgonBox
                    component="label"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed #d2d6da",
                      borderRadius: 2,
                      p: 2.5,
                      cursor: "pointer",
                      transition: "all 0.3s",
                      minHeight: "100px",
                      background: formData.homeWork ? "#f0f9ff" : "transparent",
                      "&:hover": {
                        borderColor: "#5e72e4",
                        background: "#f8f9fe",
                      },
                    }}
                  >
                    <CloudUpload sx={{ fontSize: 32, color: "#94a3b8", mb: 1 }} />
                    <ArgonTypography variant="caption" color="text" textAlign="center">
                      {formData.homeWork
                        ? `✓ ${formData.homeWork.name}`
                        : "Drop files here or browse"}
                    </ArgonTypography>
                    <ArgonTypography variant="caption" color="secondary" fontSize="10px" mt={0.5}>
                      Max size: 50MB
                    </ArgonTypography>
                    <input
                      type="file"
                      hidden
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileChange("homeWork", e.target.files[0])}
                    />
                  </ArgonBox>
                </ArgonBox>
              </Grid>

              <Grid item xs={12} sm={6}>
                <ArgonBox>
                  <ArgonTypography variant="caption" fontWeight="bold" mb={1} display="block">
                    Voice Note
                  </ArgonTypography>

                  {/* Show existing file if available */}
                  {attendance?.voiceNote && (
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="caption" color="text" mb={0.5} display="block">
                        Current File:
                      </ArgonTypography>
                      <ArgonBox
                        sx={{
                          border: "1px solid #e9ecef",
                          borderRadius: 2,
                          p: 1.5,
                          background: "#f8f9fa",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <ArgonTypography variant="caption" fontWeight="medium">
                          🎵 Audio File
                        </ArgonTypography>
                        <ArgonButton
                          component="a"
                          href={attendance.voiceNote}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="text"
                          color="info"
                          size="small"
                          sx={{ fontSize: "11px", py: 0.5, px: 1 }}
                        >
                          Play
                        </ArgonButton>
                      </ArgonBox>
                    </ArgonBox>
                  )}

                  {/* Upload section */}
                  <ArgonTypography variant="caption" color="text" mb={0.5} display="block">
                    {attendance?.voiceNote ? "Upload New Voice Note" : "Upload Voice Note"}
                  </ArgonTypography>
                  <ArgonBox
                    component="label"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed #d2d6da",
                      borderRadius: 2,
                      p: 2.5,
                      cursor: "pointer",
                      transition: "all 0.3s",
                      minHeight: "100px",
                      background: formData.voiceNote ? "#f0f9ff" : "transparent",
                      "&:hover": {
                        borderColor: "#5e72e4",
                        background: "#f8f9fe",
                      },
                    }}
                  >
                    <CloudUpload sx={{ fontSize: 32, color: "#94a3b8", mb: 1 }} />
                    <ArgonTypography variant="caption" color="text" textAlign="center">
                      {formData.voiceNote
                        ? `✓ ${formData.voiceNote.name}`
                        : "Drop files here or browse"}
                    </ArgonTypography>
                    <ArgonTypography variant="caption" color="secondary" fontSize="10px" mt={0.5}>
                      Max size: 50MB
                    </ArgonTypography>
                    <input
                      type="file"
                      hidden
                      accept="audio/*"
                      onChange={(e) => handleFileChange("voiceNote", e.target.files[0])}
                    />
                  </ArgonBox>
                </ArgonBox>
              </Grid>
            </Grid>

            <ArgonBox mt={4} display="flex" justifyContent="flex-end" gap={2}>
              <ArgonButton variant="outlined" color="secondary" onClick={handleClose}>
                Cancel
              </ArgonButton>
              <ArgonButton type="submit" variant="gradient" color="info" disabled={loading}>
                {loading ? "Updating..." : "Update Attendance"}
              </ArgonButton>
            </ArgonBox>
          </form>
        </ArgonBox>
      </ArgonBox>
    </Modal>
  );
}

export default EditAttendanceModal;
